/**
 * Warehouse and Shipments presentation projection.
 * Derives pick location/SKU and shipment carrier/status/postage from canonical
 * records. Does not write inventory, orders, shipments, or providers.
 */

import { carrierLabel } from "./carriers";
import { money } from "./format";

export function canonicalLineSku(opts: {
  lineSku?: string | null;
  variantSku?: string | null;
  productSku?: string | null;
}): string | null {
  const candidates = [opts.variantSku, opts.lineSku, opts.productSku];
  for (const value of candidates) {
    const trimmed = (value ?? "").trim();
    if (trimmed) return trimmed;
  }
  return null;
}

export type AllocationLedgerEntry = {
  entry_type: string;
  qty_change: number;
  location_id: string | null;
  location_name?: string | null;
  reference_type: string | null;
  reference_id: string | null;
  variant_id?: string | null;
};

export type PickLineInput = {
  id: string;
  sales_order_id: string;
  variant_id: string | null;
  qty_allocated: number;
  sku?: string | null;
  products?: { name?: string | null; sku?: string | null } | null;
  variants?: { sku?: string | null; option_values?: Record<string, string> | null } | null;
};

export type PickSlice = {
  line: PickLineInput;
  remaining: number;
  locationId: string | null;
  locationName: string | null;
};

function netAllocatedByLocation(opts: {
  orderId: string;
  lineId: string;
  variantId: string | null;
  ledger: AllocationLedgerEntry[];
}): Map<string, { qty: number; name: string | null }> {
  const byLoc = new Map<string, { qty: number; name: string | null }>();
  for (const row of opts.ledger) {
    if (row.entry_type !== "allocation" && row.entry_type !== "deallocation") continue;
    const loc = row.location_id;
    if (!loc) continue;
    const matchesLine = row.reference_type === "sales_order_item" && row.reference_id === opts.lineId;
    const matchesOrder =
      row.reference_type === "sales_order" &&
      row.reference_id === opts.orderId &&
      (!row.variant_id || !opts.variantId || row.variant_id === opts.variantId);
    if (!matchesLine && !matchesOrder) continue;
    const signed = row.entry_type === "deallocation" ? -Math.abs(Number(row.qty_change ?? 0)) : Number(row.qty_change ?? 0);
    const cur = byLoc.get(loc) ?? { qty: 0, name: row.location_name ?? null };
    cur.qty += signed;
    if (row.location_name) cur.name = row.location_name;
    byLoc.set(loc, cur);
  }
  return byLoc;
}

/**
 * Remaining pickable quantity per reservation location. Does not use
 * sales_orders.location_id. Split-location allocations stay as separate slices.
 */
export function pickSlicesFromReservations(opts: {
  lines: PickLineInput[];
  ledger: AllocationLedgerEntry[];
}): PickSlice[] {
  const slices: PickSlice[] = [];
  for (const line of opts.lines) {
    if (line.qty_allocated <= 0) continue;
    const byLoc = netAllocatedByLocation({
      orderId: line.sales_order_id,
      lineId: line.id,
      variantId: line.variant_id,
      ledger: opts.ledger,
    });
    const positive = [...byLoc.entries()].filter(([, v]) => v.qty > 0);
    if (positive.length === 0) {
      slices.push({ line, remaining: line.qty_allocated, locationId: null, locationName: null });
      continue;
    }
    let assigned = 0;
    positive.forEach(([locationId, v], idx) => {
      const leftover = line.qty_allocated - assigned;
      if (leftover <= 0) return;
      const isLast = idx === positive.length - 1;
      const remaining = isLast ? leftover : Math.min(v.qty, leftover);
      assigned += remaining;
      slices.push({ line, remaining, locationId, locationName: v.name });
    });
  }
  return slices;
}

export function pickLocationHeader(names: Array<string | null | undefined>): string {
  const unique = [...new Set(names.map((n) => (n ?? "").trim()).filter(Boolean))];
  if (unique.length === 0) return "No location";
  if (unique.length === 1) return unique[0];
  return unique.join(" · ");
}

export type ShipmentProjectionInput = {
  status: string;
  tracking_number?: string | null;
  shipped_at?: string | null;
  shipping_cost?: number | null;
  currency?: string | null;
  carrier_service_id?: string | null;
  carrier_account_id?: string | null;
  carrier_reference?: string | null;
  label_url?: string | null;
  label_type?: string | null;
  service_name?: string | null;
  shipping_provider?: string | null;
  provider_sync_status?: string | null;
  provider_shipment_id?: string | null;
  tracking_recorded_at?: string | null;
  carrier_services?: { name?: string | null; carrier_code?: string | null } | null;
  carrier_accounts?: { name?: string | null; carrier?: string | null } | null;
};

export function tradivoLabelPurchased(s: ShipmentProjectionInput): boolean {
  return Boolean(s.carrier_reference || s.label_url);
}

export function shipmentCarrierLabel(s: ShipmentProjectionInput): string {
  const account = (s.carrier_accounts?.name ?? "").trim();
  if (account) return account;
  const service = (s.carrier_services?.name ?? "").trim();
  if (service) return service;
  const recorded = (s.service_name ?? "").trim();
  if (recorded) return recorded;
  const provider = (s.shipping_provider ?? s.carrier_accounts?.carrier ?? s.carrier_services?.carrier_code ?? "").trim();
  if (provider) {
    const labeled = carrierLabel(provider);
    if (labeled && labeled !== "—") return labeled;
  }
  return "No carrier";
}

export function shipmentServiceLabel(s: ShipmentProjectionInput): string {
  const service = (s.carrier_services?.name ?? "").trim();
  const recorded = (s.service_name ?? "").trim();
  if (service && recorded && service !== recorded) return recorded;
  if (service) return service;
  if (recorded && recorded !== shipmentCarrierLabel(s)) return recorded;
  return "Not selected";
}

export function shipmentLifecycleComplete(s: Pick<ShipmentProjectionInput, "status" | "provider_sync_status">): boolean {
  if (["shipped", "in_transit", "delivered", "cancelled"].includes(s.status)) return true;
  return s.provider_sync_status === "confirmed";
}

export function shipmentOperatorStatus(s: ShipmentProjectionInput): { badge: string; label: string } {
  if (s.status === "delivered") return { badge: "delivered", label: "Delivered" };
  if (s.status === "in_transit") return { badge: "in_transit", label: "In transit" };
  if (s.status === "cancelled") return { badge: "cancelled", label: "Cancelled" };
  if (s.status === "exception") return { badge: "exception", label: "Exception" };
  if (s.provider_sync_status === "confirmed" || s.status === "shipped") {
    return { badge: "shipped", label: "Shipped / Confirmed" };
  }
  if (s.status === "ready") return { badge: "ready", label: "Ready" };
  if (s.status === "packed") return { badge: "packed", label: "Packed" };
  return { badge: s.status, label: s.status.replace(/_/g, " ") };
}

export function authoritativeShippedAt(s: ShipmentProjectionInput): string | null {
  if (s.shipped_at) return s.shipped_at;
  if (s.tracking_recorded_at && (s.provider_sync_status === "confirmed" || s.status === "shipped" || Boolean(s.tracking_number))) {
    return s.tracking_recorded_at;
  }
  return null;
}

export function shipmentPostageLabel(s: ShipmentProjectionInput): string {
  const cost = s.shipping_cost;
  const purchased = tradivoLabelPurchased(s);
  if (cost == null || !Number.isFinite(Number(cost))) {
    return purchased ? "—" : "Not purchased in Tradivo";
  }
  if (Number(cost) === 0 && !purchased) return "Not purchased in Tradivo";
  return `${money(cost, s.currency ?? undefined)} ${s.currency ?? ""}`.trim();
}

export function shipmentLabelStateLabel(s: ShipmentProjectionInput): string {
  if (tradivoLabelPurchased(s)) {
    if (s.label_type === "carrier") return "Carrier label";
    if (s.label_type === "internal") return "Internal label";
    return "Label purchased";
  }
  if (s.tracking_number) return "Not purchased in Tradivo";
  if (s.label_type === "internal") return "Not purchased in Tradivo";
  return "—";
}

export function warehouseViewsWriteProviders(): boolean {
  return false;
}

export function warehouseViewsMutateInventory(): boolean {
  return false;
}
